import React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Edit, Trash2, Loader2, ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { Subject, Topic } from '../../../api/subjects';

interface SubjectsListProps {
  isDark: boolean;
  subjects: Subject[];
  isLoading: boolean;
  topicsBySubject: Record<string, Topic[]>;
  expandedSubjectId: string | null;
  onEdit: (subject: Subject) => void;
  onDelete: (id: string) => void;
  onToggleTopics: (subjectId: string) => void;
  onCreateTopic: (subjectId: string) => void;
  onEditTopic: (topic: Topic) => void;
  onDeleteTopic: (topic: Topic) => void;
}

export function SubjectsList({
  isDark,
  subjects,
  isLoading,
  topicsBySubject,
  expandedSubjectId,
  onEdit,
  onDelete,
  onToggleTopics,
  onCreateTopic,
  onEditTopic,
  onDeleteTopic,
}: SubjectsListProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#0EA5A4]" />
      </div>
    );
  }

  if (subjects.length === 0) {
    return <div className="text-center py-12 text-slate-500">No subjects found</div>;
  }

  return (
    <div className="divide-y divide-slate-200 dark:divide-slate-700">
      {subjects.map((subject) => {
        const expanded = expandedSubjectId === subject.id;
        const topics = topicsBySubject[subject.id] || [];

        return (
          <div key={subject.id} className="py-3 px-2">
            <div className="flex items-center gap-3">
              <button
                onClick={() => onToggleTopics(subject.id)}
                className={cn(
                  'p-1 rounded transition-colors',
                  isDark ? 'hover:bg-slate-800 text-slate-400' : 'hover:bg-slate-100 text-slate-600',
                )}
              >
                {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>

              <div className="flex-1 min-w-0">
                <div className={cn('font-medium', isDark ? 'text-white' : 'text-[#0F172A]')}>
                  {subject.name}
                  <span className="ml-2 text-xs text-slate-500">/{subject.slug}</span>
                </div>
                {(subject.name_ru || subject.name_kiril) && (
                  <div className="text-xs text-slate-500 mt-0.5">
                    {[subject.name_ru, subject.name_kiril].filter(Boolean).join(' • ')}
                  </div>
                )}
              </div>

              <div className="text-xs text-slate-500 hidden md:block">order: {subject.sort_order}</div>

              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEdit(subject)}
                  className={cn(
                    'h-8 px-2 rounded-lg',
                    isDark ? 'text-slate-400 hover:bg-slate-800 hover:text-white' : 'text-slate-600 hover:bg-slate-100',
                  )}
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(subject.id)}
                  className={cn(
                    'h-8 px-2 rounded-lg',
                    isDark
                      ? 'text-slate-400 hover:bg-slate-800 hover:text-red-400'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-red-600',
                  )}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {expanded && (
              <div className="mt-3 ml-9 pl-4 border-l border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between mb-2">
                  <span className={cn('text-xs font-semibold uppercase', isDark ? 'text-slate-400' : 'text-slate-500')}>
                    Topics ({topics.length})
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onCreateTopic(subject.id)}
                    className="h-7 text-xs text-[#0EA5A4] hover:bg-[#0EA5A4]/10"
                  >
                    <Plus className="w-3 h-3 mr-1" /> Add topic
                  </Button>
                </div>

                {topics.length === 0 ? (
                  <div className="text-xs text-slate-500 py-2">No topics yet</div>
                ) : (
                  <ul className="space-y-1">
                    {topics.map((topic) => (
                      <li
                        key={topic.id}
                        className={cn(
                          'flex items-center justify-between py-1.5 px-2 rounded',
                          isDark ? 'hover:bg-slate-800' : 'hover:bg-slate-50',
                        )}
                      >
                        <div className="flex-1 min-w-0">
                          <span className={cn('text-sm', isDark ? 'text-slate-200' : 'text-slate-700')}>
                            {topic.name}
                          </span>
                          <span className="ml-2 text-xs text-slate-500">/{topic.slug}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onEditTopic(topic)}
                            className="h-7 px-2 text-slate-500 hover:text-[#0EA5A4]"
                          >
                            <Edit className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onDeleteTopic(topic)}
                            className="h-7 px-2 text-slate-500 hover:text-red-500"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
